import { BatchSchedule, Queue, Tag } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { IntentDetection } from 'apis/intentDetection';
import { queuesApi } from 'apis/queues';
import { Template } from 'apis/templates';

import { useIntentDetectionFetch } from '../hooks/useIntentDetectionFetch';
import { useScheduleFetch } from '../hooks/useScheduleFetch';
import { useTagFetch } from '../hooks/useTagFetch';
import { useTemplatesFetch } from '../hooks/useTemplateFetch';
import { FlowAction } from '../types';

export type FlowExtraData = {
  queues: readonly Queue[];
  tags: readonly Tag[];
  templates: readonly Template[];
  schedules: readonly BatchSchedule[];
  intentDetections: readonly IntentDetection[];
};

type FlowExtraDataFetchProps = {
  render: (data: FlowExtraData) => JSX.Element;
  actions: FlowAction[] | null;
};

/**
 * Represents a component that fetches common extra data needed for the flow and
 * provides it to its children through a render prop.
 */
export function FlowExtraDataFetch({
  render,
  actions,
}: FlowExtraDataFetchProps): JSX.Element {
  const { queues } = queuesApi.useReadAllQueuesQuery(undefined, {
    selectFromResult: ({ data }) => ({
      queues: data?.data.queues ?? [],
    }),
  });

  const tags = useTagFetch(actions);
  const templates = useTemplatesFetch(actions);
  const schedules = useScheduleFetch(actions);
  const intentDetections = useIntentDetectionFetch(actions);

  const data = useMemo((): FlowExtraData => {
    return {
      queues,
      tags,
      templates,
      schedules,
      intentDetections,
    };
  }, [queues, tags, templates, schedules, intentDetections]);

  return render(data);
}
